import { CloseButton } from 'components/CloseButton'
import Row, { RowBetween } from 'components/Layout/Row'
import { Path } from 'config/constants/paths'
import { useTranslation } from 'contexts/Localization'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import useToast from 'hooks/useToast'
import { toastTypes } from 'components/Toast'
import { useCallback, useState, useRef, useEffect, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { useGotchiAuth } from 'state/vvsgotchi/hooks/useAuth'
import useGotchiOwner from 'state/vvsgotchi/hooks/useGotchiOwner'
import { useTheme } from 'styled-components'
import { GetGotchiOwnerDocument, useUpdateTeamMembersMutation } from 'vvs-gotchi-graphql'
import { Box, Flex, Link, Text, UserDisabledIcon, UserIcon, TimerIcon, useModal } from 'vvs-uikit'
import ActionButton from 'views/VvsGotchi/components/ActionButton'
import ScreenFooter from 'views/VvsGotchi/components/ScreenFooter'
import { RunningModal } from 'views/VvsGotchi/components/Modal'
import getTeamSelectUnlockAt from 'views/VvsGotchi/utils/getTeamSelectUnlockAt'
import historyBackOrReplace from 'utils/historyBackOrReplace'
import { getVVSMinerMoleMintedUrl } from 'utils/mintedNftCollectionUrls'
import { useHourCountdown } from 'hooks/useCountdown'
import { ExpeditionTeamSelectCheckRow } from '../components/ExpeditionTeamRow'
import { MAX_TEAM_SIZE } from '../config'
import { LightPageContainer } from '../styles'
import { StyledTeamSelectContainer } from './styles'

const GotchiTeamSelect = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { account } = useActiveWeb3React()
  const { toastVVSGotchi } = useToast()
  const [, ensureAuth] = useGotchiAuth()
  const history = useHistory()
  const { owner, team, moles, loading, refresh } = useGotchiOwner()
  const [selectedIds, setSelectedIds] = useState(() => team?.map((m) => m.tokenId) ?? [])
  const [updateTeamMembers] = useUpdateTeamMembersMutation()

  const hasMoleSelectedChanged =
    selectedIds.length !== team?.length || selectedIds.some((id) => team?.map((m) => m.tokenId).indexOf(id) === -1)
  const noMoles = !moles || moles?.length <= 0

  const runningModalSwitches = useModal(
    <RunningModal>{t('Loading...')}</RunningModal>,
    false,
    false,
    'vvsgotchiFeeding',
  )
  const runningModalSwitchesRef = useRef(runningModalSwitches)
  runningModalSwitchesRef.current = runningModalSwitches

  const onContinue = useCallback(async () => {
    const [openUpdatingModal, closeUpdatingModal] = runningModalSwitchesRef.current

    await ensureAuth()
    openUpdatingModal()
    try {
      await updateTeamMembers({
        variables: {
          owner: account,
          tokenIds: selectedIds,
        },
        refetchQueries: [GetGotchiOwnerDocument],
      })
      await refresh()
      history.replace(Path.VVS_GOTCHI_TEAM)
      toastVVSGotchi(
        t('Successfully updated your team ( ͡° ͜ʖ ͡° )'),
        toastTypes.INFO,
        <UserDisabledIcon fill="black" width="16px" />,
      )
    } catch (err) {
      console.error(err)
    } finally {
      closeUpdatingModal()
    }
  }, [account, updateTeamMembers, history, selectedIds, refresh, ensureAuth, toastVVSGotchi, t])

  useEffect(() => {
    if (team) {
      setSelectedIds((ids) => [...team.map((m) => m.tokenId).filter((id) => ids.indexOf(id) === -1), ...ids])
    }
  }, [team])

  const teamSelectUnlockAt = useMemo(() => getTeamSelectUnlockAt(owner?.lockedMoles, team), [owner?.lockedMoles, team])

  if ((!moles || !team) && loading) {
    return (
      <LightPageContainer>
        <Flex flex="1" justifyContent="center" alignItems="center">
          {t('Loading...')}
        </Flex>
      </LightPageContainer>
    )
  }

  const teamSelectLocked = teamSelectUnlockAt !== undefined

  return (
    <LightPageContainer>
      {noMoles ? (
        <>
          <Flex justifyContent="end" mt={3} mr={3}>
            <CloseButton fill="black" onClick={() => historyBackOrReplace(history, Path.VVS_GOTCHI_TEAM)} />
          </Flex>
          <Flex
            background={theme.colors.paleYellow}
            height="100%"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
          >
            <img src="/images/vvsGotchi/vvs_gotchi_mole_row.png" alt="vvsgotchi mole row" />
            <Text mt={3} fontSize="20px" bold color="black">
              {t('No Moles Found')}
            </Text>
            <Text color="darkGrey" textAlign="center" maxWidth="365px" mb={1} px={3}>
              {t('It looks like you don’t own any Miner Moles NFT yet in your wallet! Adopt one now on')}
            </Text>
            <Link href={getVVSMinerMoleMintedUrl()} target="_self" rel="noreferer noopener">
              {t('Marketplace.')}
            </Link>
          </Flex>
        </>
      ) : (
        <>
          <StyledTeamSelectContainer>
            <Flex mb="18px">
              <Box flex="1" pt={3}>
                <Text color="darkestNightBlue" bold fontSize={20}>
                  {t('Choose Team Members')}
                </Text>
              </Box>
              <CloseButton fill="black" onClick={() => historyBackOrReplace(history, Path.VVS_GOTCHI_TEAM)} />
            </Flex>
            {teamSelectLocked ? (
              <LockCountdown time={teamSelectUnlockAt} />
            ) : (
              <>
                <Text color="black" fontSize="13px" fontWeight="700" py={3}>
                  {t('When you add a Mole to your team, it will be subject to a 72 hour locking period. ')}
                  <Link
                    inline
                    linkColor={theme.colors.primary}
                    href="https://docs.vvs.finance/product-guides/vvs-gotchi/team-page#assembling-your-team"
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    {t('Learn more here')}
                  </Link>
                </Text>
                <Row>
                  <ExpeditionTeamSelectCheckRow selectedIds={selectedIds} setSelectedIds={setSelectedIds} />
                </Row>
              </>
            )}
          </StyledTeamSelectContainer>
          <ScreenFooter>
            {!teamSelectLocked && (
              <RowBetween mb={3}>
                <Text color="white">{t('%num% out of 5 added', { num: selectedIds.length })}</Text>
                <Flex>
                  {new Array(selectedIds.length)
                    .fill(null)
                    .map((_, i) => i)
                    .map((i) => (
                      <UserIcon key={i} />
                    ))}
                  {new Array(MAX_TEAM_SIZE - selectedIds.length)
                    .fill(null)
                    .map((_, i) => i)
                    .map((i) => (
                      <UserDisabledIcon key={i} />
                    ))}
                </Flex>
              </RowBetween>
            )}
            <ActionButton disabled={teamSelectLocked || !hasMoleSelectedChanged} onClick={onContinue}>
              {t('Continue')}
            </ActionButton>
          </ScreenFooter>
        </>
      )}
    </LightPageContainer>
  )
}

export default GotchiTeamSelect

const LockCountdown = ({ time }: { time: number }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { refresh } = useGotchiOwner()
  const timeExcerptMap = useHourCountdown(time, refresh)

  return (
    <Flex flex="1" flexDirection="column" justifyContent="center" alignItems="center">
      <TimerIcon width="25px" height="30px" fill={theme.colors.darkestNightBlue} mb={3} />
      <Text color="black" fontSize="13px" fontWeight="700" mb={2}>
        {t('You can edit your team member in')}
      </Text>
      <Text color="primary" fontWeight="500">
        {timeExcerptMap.h > 0 ? t('%h%hr %m%m %s%s', timeExcerptMap) : t('%m%m %s%s', timeExcerptMap)}
      </Text>
    </Flex>
  )
}
